import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ScopeUnit } from "../../util/slopifyScopeMap";
import { getScopeUnits } from "../../util/slopifyScopeMap";

export interface ScopeContextValue {
  modelName: string;
  availableUnits: ScopeUnit[];
  selectedUnitIds: string[];
  selectedUnits: ScopeUnit[];
  scopeFields: string[];
  isRailActive: boolean;
  isPickMode: boolean;
  isUnitSelected: (unitId: string) => boolean;
  isFieldInSelectedScope: (fieldName: string) => boolean;
  toggleUnit: (unitId: string) => void;
  replaceScope: (unitIds: string[]) => void;
  clearScope: () => void;
  setPickMode: (next: boolean) => void;
}

const defaultValue: ScopeContextValue = {
  modelName: "",
  availableUnits: [],
  selectedUnitIds: [],
  selectedUnits: [],
  scopeFields: [],
  isRailActive: false,
  isPickMode: false,
  isUnitSelected: () => false,
  isFieldInSelectedScope: () => false,
  toggleUnit: () => undefined,
  replaceScope: () => undefined,
  clearScope: () => undefined,
  setPickMode: () => undefined,
};

const ScopeContext = createContext<ScopeContextValue>(defaultValue);

interface ScopeProviderProps {
  modelName: string;
  active?: boolean;
  /** Live array lengths — drive per-item ("Answer N") unit enumeration. */
  listAnswersCount?: number;
  answersCount?: number;
  questionsCount?: number;
  children: ReactNode;
}

export const ScopeProvider = ({
  modelName,
  active = false,
  listAnswersCount,
  answersCount,
  questionsCount,
  children,
}: ScopeProviderProps) => {
  const availableUnits = useMemo(
    () =>
      getScopeUnits(modelName, {
        listAnswers: listAnswersCount,
        answers: answersCount,
        questions: questionsCount,
      }),
    [modelName, listAnswersCount, answersCount, questionsCount],
  );
  const [selectedUnitIds, setSelectedUnitIds] = useState<string[]>([]);
  const [isPickMode, setPickMode] = useState<boolean>(false);

  useEffect(() => {
    setSelectedUnitIds((prev) => {
      if (prev.length === 0) return prev;
      const valid = new Set(availableUnits.map((u) => u.id));
      const pruned = prev.filter((id) => valid.has(id));
      return pruned.length === prev.length ? prev : pruned;
    });
  }, [availableUnits]);

  const toggleUnit = useCallback((unitId: string) => {
    setSelectedUnitIds((prev) =>
      prev.includes(unitId) ? prev.filter((id) => id !== unitId) : [...prev, unitId],
    );
  }, []);

  const replaceScope = useCallback((unitIds: string[]) => {
    setSelectedUnitIds(unitIds);
  }, []);

  const clearScope = useCallback(() => {
    setSelectedUnitIds([]);
  }, []);

  const selectedUnits = useMemo(
    () =>
      selectedUnitIds
        .map((id) => availableUnits.find((u) => u.id === id))
        .filter((u): u is ScopeUnit => Boolean(u)),
    [selectedUnitIds, availableUnits],
  );

  const scopeFields = useMemo(
    () => Array.from(new Set(selectedUnits.flatMap((u) => u.schemaFields))),
    [selectedUnits],
  );

  const selectedFieldSet = useMemo(() => new Set(scopeFields), [scopeFields]);

  const value: ScopeContextValue = useMemo(
    () => ({
      modelName,
      availableUnits,
      selectedUnitIds,
      selectedUnits,
      scopeFields,
      isRailActive: active,
      isPickMode: active && isPickMode,
      isUnitSelected: (unitId: string) => selectedUnitIds.includes(unitId),
      isFieldInSelectedScope: (fieldName: string) => selectedFieldSet.has(fieldName),
      toggleUnit,
      replaceScope,
      clearScope,
      setPickMode,
    }),
    [
      modelName,
      active,
      availableUnits,
      selectedUnitIds,
      selectedUnits,
      scopeFields,
      selectedFieldSet,
      isPickMode,
      toggleUnit,
      replaceScope,
      clearScope,
    ],
  );

  return <ScopeContext.Provider value={value}>{children}</ScopeContext.Provider>;
};

export const useScopeContext = (): ScopeContextValue => useContext(ScopeContext);
